/// <reference types="vite/client" />
/**
 * Tenant registry for the multi-tenant admin. Each school / alumni org is a SILOED
 * Supabase project; the app resolves which one to use from the request hostname
 * (`<slug>.codeblacksda.com`). Anon keys are PUBLIC (RLS is the real gate), so they
 * are safe to commit. `resolveBoot` is pure (no I/O) for unit testing; `getBootDecision`
 * is the thin impure wrapper that reads `window.location.hostname`.
 *
 * The default tenant (MUAA) sources its url/anon from the already-baked VITE_SUPABASE_*
 * env (unchanged from single-tenant builds). Additional tenants are committed as literals.
 */

export interface Tenant {
  /** Subdomain slug, e.g. "muaa" for muaa.codeblacksda.com */
  slug: string
  /** Human label, e.g. "Miami Union Adventist Academy" */
  label: string
  /** Supabase project URL */
  url: string
  /** Supabase anon/publishable key — PUBLIC, safe to commit */
  anonKey: string
  /** Optional per-tenant crest asset path under public/ */
  crest?: string
}

/** Root domain the app is hosted under. */
export const ROOT_DOMAIN = 'codeblacksda.com'

/** Tenant used for local dev, previews, apex redirects, and non-root hosts. */
export const DEFAULT_TENANT_SLUG = 'muaa'

export const TENANTS: Record<string, Tenant> = {
  muaa: {
    slug: 'muaa',
    label: 'Miami Union Adventist Academy',
    // Default tenant reads the already-baked env (same value prod builds use today,
    // sourced from mobile/eas.json by scripts/launch-admin-dashboards.sh).
    url: (import.meta.env.VITE_SUPABASE_URL as string) ?? '',
    anonKey: (import.meta.env.VITE_SUPABASE_ANON_KEY as string) ?? '',
    crest: '/crest.png',
  },
  // Add more tenants here as committed literals, e.g.:
  // otherschool: {
  //   slug: 'otherschool',
  //   label: 'Other Academy',
  //   url: 'https://xxxxxxxx.supabase.co',
  //   anonKey: '<public anon/publishable key for that project>',
  //   crest: '/crest.png',
  // },
}

export type BootDecision =
  | { kind: 'render'; tenant: Tenant }
  | { kind: 'redirect'; to: string }
  | { kind: 'unknown'; slug: string }

/**
 * Pure classification of a hostname into a boot decision. Rules (spec §6):
 * - non-root host (localhost, 127.0.0.1, *.pages.dev, anything not *.codeblacksda.com)
 *   → render the DEFAULT tenant (preserves local dev + previews)
 * - apex `codeblacksda.com` or `www.` → redirect to the default tenant subdomain
 * - `<slug>.codeblacksda.com` present in the registry → render that tenant
 * - `<slug>.codeblacksda.com` NOT in the registry → unknown (no silent fallback)
 */
export function resolveBoot(
  hostname: string,
  registry: Record<string, Tenant> = TENANTS,
): BootDecision {
  const host = (hostname ?? '').toLowerCase().trim()
  const defaultTenant = registry[DEFAULT_TENANT_SLUG]

  if (!defaultTenant) {
    throw new Error(
      `resolveBoot: registry is missing the default tenant "${DEFAULT_TENANT_SLUG}"`,
    )
  }

  const dotRoot = '.' + ROOT_DOMAIN

  const isRoot = host === ROOT_DOMAIN || host.endsWith(dotRoot)
  if (!isRoot) return { kind: 'render', tenant: defaultTenant }

  if (host === ROOT_DOMAIN) {
    return { kind: 'redirect', to: `https://${DEFAULT_TENANT_SLUG}.${ROOT_DOMAIN}` }
  }

  const sub = host.slice(0, host.length - dotRoot.length)
  const slug = sub.split('.')[0]
  if (slug === 'www') {
    return { kind: 'redirect', to: `https://${DEFAULT_TENANT_SLUG}.${ROOT_DOMAIN}` }
  }

  const tenant = registry[slug]
  return tenant ? { kind: 'render', tenant } : { kind: 'unknown', slug }
}

/** Thin impure wrapper: read the live hostname. */
export function getBootDecision(): BootDecision {
  return resolveBoot(window.location.hostname)
}

/**
 * The tenant the admin UI should render under (crest, label, title — spec §11).
 * Pure sibling of `resolveBoot`. Inside the mounted app the decision is always
 * `render` (an apex/www `redirect` never mounts the app, and an `unknown` slug
 * renders the standalone UnknownOrganization page — see main.tsx), but fall back
 * to the default tenant for those kinds so theming can never crash.
 */
export function resolveActiveTenant(
  hostname: string,
  registry: Record<string, Tenant> = TENANTS,
): Tenant {
  const boot = resolveBoot(hostname, registry)
  return boot.kind === 'render' ? boot.tenant : registry[DEFAULT_TENANT_SLUG]
}

/** Thin impure wrapper: resolve the render tenant from the live hostname. */
export function getActiveTenant(): Tenant {
  return resolveActiveTenant(window.location.hostname)
}

/**
 * Browser-tab title for a boot decision (set at runtime in main.tsx, replacing
 * the neutral static fallback in index.html) so a second tenant's tab never
 * reads "MUAA". The redirect case never mounts, so its title is never shown.
 */
export function bootDocumentTitle(boot: BootDecision): string {
  switch (boot.kind) {
    case 'render':
      return `${boot.tenant.label} Admin`
    case 'unknown':
      return 'Unknown organization'
    case 'redirect':
      return 'Admin'
  }
}

/**
 * Re-brand a Refine auto-generated document title with the resolved tenant.
 * Refine's `<DocumentTitleHandler>` owns `document.title` once the app mounts and
 * suffixes every page with its default app name ("Refine"); wiring this in swaps
 * that suffix so tabs read e.g. "Members | Miami Union Adventist Academy Admin"
 * instead of leaking "Refine" (and never MUAA's name for a second tenant).
 */
export function brandDocumentTitle(autoGeneratedTitle: string, tenant: Tenant): string {
  const suffix = `${tenant.label} Admin`
  const DEFAULT_APP_NAME = 'Refine'
  return autoGeneratedTitle.endsWith(DEFAULT_APP_NAME)
    ? autoGeneratedTitle.slice(0, -DEFAULT_APP_NAME.length) + suffix
    : autoGeneratedTitle
}
